import React, { FC, useContext } from "react";
import { CommunicationChannel, ICommunicationChannel, IPreferences } from "../../logic/api";
import { GridLegacy as Grid, useMediaQuery, useTheme } from "@mui/material";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";
import EditableGroup, { EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import styles from "./styles";
import { SessionContext } from "../../utility/useSession";
import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import { useTranslation } from "react-i18next";

export interface ICommunicationSettingsProps {
  onSave: (newPrefs: Partial<IPreferences>) => Promise<boolean>;
  label: string;
}

export const CommunicationSettings: FC<ICommunicationSettingsProps> = ({
  onSave,
  label,
}) => {
  const session = useContext(SessionContext);

  if (session.current.site === null) {
    return null;
  }

  const channels = session.current.site.preferences.collectionReceiptOptions?.channels || [];

  return <Grid item xs={12}>
    <NotchedCard label={label} style={styles.card}>
      <EditableGroup<IPreferences>
        id="devices"
        testId="communication-settings-group"
        editType={EditableGroupType.switchableEditing}
        data={session.current.site.preferences}
        onDataChanged={(data) => onSave({
          collectionReceiptOptions: data.collectionReceiptOptions
        })}
      >
        <Grid container>
          {channels.map((channel, index) => <CommunicationChannelSettings key={index} channelType={channel.type} />)}
        </Grid>
      </EditableGroup>
    </NotchedCard>
  </Grid>
};

interface ICommunicationChannelSettingsProps {
  channelType: CommunicationChannel;
}

const CommunicationChannelSettings: FC<ICommunicationChannelSettingsProps> = ({
  channelType,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("xs"));

  const getChannel = (data: IPreferences, type: CommunicationChannel): ICommunicationChannel => {
    data.collectionReceiptOptions!.channels = data.collectionReceiptOptions!.channels || [];
    const channel = data.collectionReceiptOptions?.channels.find((c) => c.type === type);

    // Although channel will never be undefined, we need to check for it to satisfy TypeScript
    if (!channel) {
      data.collectionReceiptOptions!.channels.push({ type, enabled: false, onByDefault: false });
    }

    return data.collectionReceiptOptions!.channels.find((c) => c.type === type)!;
  }

  const typeName = CommunicationChannel[channelType];
  const labelStyle = isSmallDevice
    ? { textAlign: "center" as const }
    : { paddingTop: 40 };

  return <Grid container>
    <Grid item sm={3} xs={12}>
      <div style={labelStyle}><strong>{t(`settings.${typeName}`)}</strong></div>
    </Grid>

    <Grid item sm={2} xs={6}>
      <EditableField<IPreferences, boolean>
        useFullWidthWhenEditing={false}
        inputType={EditableFieldTypes.switch}
        label={t("settings.available")}
        onProvide={(data) => Boolean(getChannel(data, channelType).enabled)}
        onChange={(data, value) => getChannel(data, channelType).enabled = value}
        testId={`settings-communication-channel-${channelType}-enabled-switch`}
      />
    </Grid>

    <Grid item sm={7} xs={6}>
      <EditableField<IPreferences, boolean>
        useFullWidthWhenEditing={false}
        inputType={EditableFieldTypes.switch}
        label={t("settings.preSelectedAtCheckIn")}
        onProvide={(data) => getChannel(data, channelType).onByDefault}
        onChange={(data, value) => getChannel(data, channelType).onByDefault = value}
        testId={`settings-communication-channel-${channelType}-on-by-default-switch`}
      />
    </Grid>
  </Grid>
}
